/**
 * FiasAIConfirmationDialog — the explicit-approval window for a Fias AI
 * action. Replaces the cramped inline card with a proper modal: a friendly
 * header, the fields the user may adjust (pre-filled from the model's
 * proposal), the remaining details read-only, and Approve / Reject.
 *
 * Editable values are sent back as `edits` (only changed keys); the SERVER
 * overlays them onto the persisted proposal and re-parses through the tool's
 * schema — the client never bypasses validation. Clicking OUTSIDE the window
 * does nothing (a stray click while composing must not whisk away the approve
 * button); dismissal is deliberate only — the X or Esc. Even then it only
 * dismisses, never rejects, and the inline card reopens it.
 */

import { useEffect, useState } from 'react';
import { Sparkles, Check, X, ShieldQuestion } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { AssistantConfirmationPreview } from './agent-types';
import { humanizeLabel } from './lib/humanizeLabel';

export interface ConfirmationDialogPending {
  interactionId: string;
  toolName: string;
  riskTier: string;
  reason: string;
  preview: AssistantConfirmationPreview;
}

interface Props {
  pending: ConfirmationDialogPending | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Approve — `edits` carries only the fields the user changed. */
  onApprove: (edits?: Record<string, string>) => void;
  /** Reject — optional note fed back to the model. */
  onReject: (feedback?: string) => void;
  isSubmitting?: boolean;
}

/** A short, non-technical explanation of WHY the confirmation is being asked. */
function reasonText(reason: string): string {
  switch (reason) {
    case 'tainted_session':
      return 'Some of these details came from your inbox or messages — please review them before saving.';
    case 'requires_confirmation':
      return 'Please review the details below before Fias continues.';
    default:
      return 'Review the details below, make any changes, then approve.';
  }
}

export function FiasAIConfirmationDialog({
  pending,
  open,
  onOpenChange,
  onApprove,
  onReject,
  isSubmitting,
}: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState(false);
  const [feedback, setFeedback] = useState('');

  const interactionId = pending?.interactionId;
  const editable = pending?.preview.editable ?? [];

  // Reset local edit state whenever a NEW confirmation arrives (keyed on the
  // interaction id — a new id always carries a fresh preview).
  useEffect(() => {
    const init: Record<string, string> = {};
    for (const f of pending?.preview.editable ?? []) init[f.key] = f.value;
    setValues(init);
    setRejecting(false);
    setFeedback('');
  }, [interactionId, pending?.preview.editable]);

  if (!pending) return null;

  const readOnlyFields = pending.preview.fields;

  const handleApprove = () => {
    // Send only the fields the user actually changed; unchanged fields stay as
    // the model proposed them (server re-parses the merged object regardless).
    const edits: Record<string, string> = {};
    for (const f of editable) {
      if ((values[f.key] ?? '') !== f.value) edits[f.key] = values[f.key] ?? '';
    }
    onApprove(Object.keys(edits).length > 0 ? edits : undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="fias-ai-confirmation-dialog"
        // A click on the backdrop must NOT close the window — losing an
        // in-progress approval to a stray click is exactly what this guards.
        // Deliberate dismissal (X / Esc) still works.
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Sparkles className="h-4 w-4" />
            </span>
            {humanizeLabel(pending.preview.title)}
          </DialogTitle>
          <DialogDescription>{reasonText(pending.reason)}</DialogDescription>
        </DialogHeader>

        {/* px/py + matching -mx/-my give the inputs' focus ring (ring-2 +
            ring-offset-2 ≈ 4px) room inside the scroll box so it isn't clipped
            by overflow-y-auto, while the negative margins keep content aligned
            with the header/footer (absorbed by DialogContent's p-6). */}
        <div className="-mx-2 -my-1 max-h-[55vh] space-y-4 overflow-y-auto px-2 py-2">
          {editable.length > 0 && (
            <div className="space-y-3">
              {editable.map((f) => {
                const id = `${pending.interactionId}-${f.key}`;
                return (
                  <div key={f.key} className="space-y-1">
                    <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">
                      {humanizeLabel(f.label)}
                    </Label>
                    {f.input === 'textarea' ? (
                      <Textarea
                        id={id}
                        value={values[f.key] ?? ''}
                        placeholder={f.placeholder}
                        maxLength={8000}
                        rows={3}
                        onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      />
                    ) : (
                      <Input
                        id={id}
                        type={f.input === 'email' ? 'email' : f.input === 'tel' ? 'tel' : 'text'}
                        value={values[f.key] ?? ''}
                        placeholder={f.placeholder}
                        maxLength={8000}
                        onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {readOnlyFields.length > 0 && (
            <dl className="space-y-1 rounded-md border bg-muted/30 p-3 text-sm">
              {readOnlyFields.map((f) => (
                <div key={f.label} className="flex gap-2">
                  <dt className="min-w-24 flex-shrink-0 font-medium text-muted-foreground">
                    {humanizeLabel(f.label)}
                  </dt>
                  <dd className="min-w-0 break-words">{f.value}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldQuestion className="h-3.5 w-3.5 flex-shrink-0" />
            <span>
              Acting as{' '}
              <span className="font-medium text-foreground/80">{pending.preview.actingAs}</span>
            </span>
          </div>

          {rejecting && (
            <div className="space-y-1">
              <Label
                htmlFor={`${pending.interactionId}-reject-reason`}
                className="text-xs font-medium text-muted-foreground"
              >
                Reason (optional)
              </Label>
              <Input
                id={`${pending.interactionId}-reject-reason`}
                value={feedback}
                maxLength={2000}
                placeholder="Tell Fias why, so it can try a different approach"
                onChange={(e) => setFeedback(e.target.value)}
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            disabled={isSubmitting}
            onClick={() => (rejecting ? onReject(feedback || undefined) : setRejecting(true))}
          >
            <X className="mr-1 h-4 w-4" />
            {rejecting ? 'Confirm reject' : 'Reject'}
          </Button>
          {!rejecting && (
            <Button disabled={isSubmitting} onClick={handleApprove}>
              <Check className="mr-1 h-4 w-4" /> Approve
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
